import { SelectionModel } from "@angular/cdk/collections";
import { HttpResponse } from "@angular/common/http";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
  viewChild,
} from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatOption } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { MatListOption, MatSelectionListChange } from "@angular/material/list";
import { MatPaginator } from "@angular/material/paginator";
import { MatSelect } from "@angular/material/select";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatStepper } from "@angular/material/stepper";
import { cloneDeep } from "lodash";
import { forkJoin, Observable, Subject } from "rxjs";
import { debounceTime, filter, take, takeUntil, tap } from "rxjs/operators";
import { sortByStringWithEmptyAtEnd } from "src/app/shared/helpers/helper-functions";
import {
  CONFIRM_DIALOG_CONFIRM_ACTION,
  CONFIRM_DIALOG_FORM_SET_ACTIVE,
  CONFIRM_DIALOG_FORM_SET_ALL_OBJECT,
  CONFIRM_DIALOG_FORM_SET_ALL_SELECTED_OBJ,
  CONFIRM_DIALOG_FORM_SET_INACTIVE,
  FormTargetTypes,
  SNACKBAR_FORM_ASSIGN_ERROR,
  SNACKBAR_FORM_ASSIGN_SUCCESS,
} from "../../../locale/consts.en";
import { InstallationsService } from "../../customer/installations.service";
import { MasterDataService } from "../../master-data/master-data-service";
import {
  Branch,
  Customer,
  Department,
  Installation,
  OrderType,
  Product,
  ProductCategory,
  Status,
} from "../../master-data/master-data.model";
import { ConfirmDialogModel } from "../../shared/confirm-dialog/confim-dialog.model";
import { ConfirmDialogComponent } from "../../shared/confirm-dialog/confirm-dialog.component";
import { FormAssignPermDialogComponent } from "../form-assign-perm-dialog/form-assign-perm-dialog.component";
import { FormLoaderComponent } from "../form-loader/form-loader.component";
import { FormsAPIService } from "../forms-api.service";
import {
  ActionType,
  ChangeFormPermissionsCommand,
  FormSetActiveCommandAPI,
  FormTemplate,
  PermissionInfo,
  PermissionObject,
  PermissionStringMap,
  PermissionType,
  UpdateFormFileCommand,
} from "../forms.model";

@Component({
  selector: "app-form-assign",
  templateUrl: "./form-assign.component.html",
  styleUrls: ["./form-assign.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormAssignComponent implements OnInit, AfterViewInit, OnDestroy {
  private formsAPIService = inject(FormsAPIService);
  installationsService = inject(InstallationsService);
  private masterDataService = inject(MasterDataService);
  private snackBar = inject(MatSnackBar);
  dialog = inject(MatDialog);
  private fb = inject(FormBuilder);

  readonly formLoader = viewChild(FormLoaderComponent);
  readonly stepper = viewChild<MatStepper>("stepper");
  readonly paginator = viewChild(MatPaginator);
  readonly matRef = viewChild<MatSelect>("matRef");
  private destroyed$ = new Subject();
  form: FormTemplate;

  showOnlyActive = true;

  installationSearchText = "";

  selectionForm = this.fb.group({
    actionType: [null, [Validators.required]],
    selectedPermissionType: ["", [Validators.required]],
    searchText: [null],
  });
  isEditable = false;
  formSelected = false;

  ActionType = ActionType;
  PermissionType = PermissionType;
  formTargetTypes = FormTargetTypes;

  formPermissionsMap: PermissionInfo;

  savePermissionMap: PermissionStringMap = {
    excluded: [],
    included: [],
  };
  editPermissionMap: PermissionStringMap = {
    excluded: [],
    included: [],
  };

  isExcludedMode = false;

  file: any;
  base64textString = "";

  selectedAction: ActionType = null;
  permissionType: PermissionType = null;

  editIncludedMemory: PermissionObject[] = [];
  editExcludedMemory: PermissionObject[] = [];
  saveIncludedMemory: PermissionObject[] = [];
  saveExcludedMemory: PermissionObject[] = [];

  selectedPermissionObjectIncluded: PermissionObject[] = [];
  selectedPermissionObjectExcluded: PermissionObject[] = [];

  currentObjectList: any; //Customer[] | Branch[] | Department[] | OrderType[] | Product[] | ProductCategory[];

  installations$: Observable<Installation[]>;
  customers: Customer[] = [];
  prodCategories: ProductCategory[];
  products: Product[];
  orderTypes: OrderType[];
  departments: Department[];
  branches: Branch[];
  statuses: Status[];
  firstSelect = true;

  constructor() {
    const installationsService = this.installationsService;

    this.installations$ = installationsService.installations$;
  }

  compareFn(c1: any, c2: any): boolean {
    return c1 && c2 && c1.uuid === c2.uuid;
  }

  ngOnInit() {
    this.initMasterData();
    this.initForm();
    this.initSearch();
  }

  private initMasterData(): void {
    console.log(">>> START INIT MASTERDATA");

    forkJoin({
      types: this.masterDataService.getTypes().pipe(take(1)),
      statuses: this.masterDataService.getStatuses().pipe(take(1)),
      departments: this.masterDataService.getDepartments().pipe(take(1)),
      branches: this.masterDataService.getBranches().pipe(take(1)),
      productCategories: this.masterDataService
        .getProductCategories()
        .pipe(take(1)),
      products: this.masterDataService.getProducts().pipe(take(1)),
      customers: this.masterDataService.getCustomers().pipe(take(1)),
    }).subscribe({
      next: (data) => {
        this.orderTypes = data.types;
        this.statuses = data.statuses;
        this.departments = data.departments;
        this.branches = data.branches;
        this.prodCategories = data.productCategories;
        this.products = data.products;
        this.customers = data.customers;
        // All properties are assigned here
        console.log("Masterdata load complete", data);
      },
      error: (err) => {
        console.error("Masterdata load error:", err);
      },
      complete: () => {
        console.log("Masterdata load complete");
      },
    });
  }
  private initForm() {
    this.form = new FormTemplate("", [], [], null);
  }

  ngAfterViewInit() {
    this.paginator()
      .page.pipe(
        takeUntil(this.destroyed$),
        tap(() => this.loadInstallationsPage()),
      )
      .subscribe();
  }

  onShowOnlyActiveChange($event: MatSlideToggleChange) {
    this.showOnlyActive = $event.checked;
  }

  onStepChange() {
    this.isEditable = false; // disable editing after each step
    // console.log(this.form);
  }

  onFormSelected(form: FormTemplate) {
    if (form !== null) {
      this.form = form;
      this.formSelected = true;

      this.formsAPIService.formGetPermissions(form.uuid).subscribe((val) => {
        this.formPermissionsMap = val;
        // console.log(this.formPermissionsMap);
        if (this.formPermissionsMap != null) {
          if (this.formPermissionsMap.edit != null) {
            if (this.formPermissionsMap.edit.included != null) {
              this.formPermissionsMap.edit.included.forEach((arr) =>
                arr.forEach((value) =>
                  this.editIncludedMemory.push(Object.assign(value)),
                ),
              );
            }
            if (this.formPermissionsMap.edit.excluded != null) {
              this.formPermissionsMap.edit.excluded.forEach((value) =>
                this.editExcludedMemory.push(Object.assign(value)),
              );
            }
          }
          if (this.formPermissionsMap.save != null) {
            if (this.formPermissionsMap.save.included != null) {
              this.formPermissionsMap.save.included.forEach((arr) =>
                arr.forEach((value) =>
                  this.saveIncludedMemory.push(Object.assign(value)),
                ),
              );
            }
            if (this.formPermissionsMap.save.excluded != null) {
              this.formPermissionsMap.save.excluded.forEach((value) =>
                this.saveExcludedMemory.push(Object.assign(value)),
              );
            }
          }
        }
        this.firstSelect = true;
      });
    } else {
      this.clearForm();
      this.formSelected = false;

      this.selectedPermissionObjectExcluded.splice(0);
      this.selectedPermissionObjectIncluded.splice(0);
      this.saveExcludedMemory.splice(0);
      this.saveIncludedMemory.splice(0);
      this.editExcludedMemory.splice(0);
      this.editIncludedMemory.splice(0);
      this.matRef().options.forEach((data: MatOption) => data.deselect());
      this.selectedAction = null;
      this.firstSelect = true;
    }
  }

  onGoToPrevious() {
    this.isEditable = true;
    setTimeout(() => {
      this.stepper().previous();
    }, 1);
  }

  getTemplates() {
    this.formsAPIService.getAll().subscribe();
  }

  isFormSelected() {
    return this.formSelected;
  }

  clearForm() {
    this.form = new FormTemplate("", [], [], [], null);
    // this.formLoader.clearRefresh();
    this.base64textString = "";
    this.file = null;
  }

  initSearch() {
    this.selectionForm.controls.searchText.valueChanges
      .pipe(
        filter((search) => !!search && search.length > 2),
        debounceTime(800),
        takeUntil(this.destroyed$),
      )
      .subscribe((search: string) => {
        this.installationSearchText = search;
        this.loadInstallationsPage();
      });
  }

  private loadInstallationsPage() {
    this.installationsService.getInstallationsForCustomer(
      null,
      this.installationSearchText,
      this.paginator().pageIndex,
      this.paginator().pageSize,
    );
  }

  openInput() {
    document.getElementById("fileInput").click();
  }

  fileChange(e: any) {
    console.log("File change");
    this.file = e.target.files[0];
    this.uploadDocument(this.file);
  }

  uploadDocument(file) {
    const fileReader = new FileReader();
    fileReader.onload = this.handleReaderLoaded.bind(this);
    fileReader.readAsBinaryString(file);
  }

  handleReaderLoaded(readerEvt) {
    const binaryString = readerEvt.target.result;
    this.base64textString = btoa(binaryString);
  }

  httpResponseReaction(response: HttpResponse<any>) {
    if (response.status === 200 || response.status === 201) {
      this.snackBar.open(SNACKBAR_FORM_ASSIGN_SUCCESS, "", {
        duration: 2000,
        panelClass: "app-snackbar-success",
      });
      this.getTemplates();
    } else {
      this.snackBar.open(SNACKBAR_FORM_ASSIGN_ERROR, "", {
        duration: 2000,
        panelClass: "app-snackbar-alert",
      });
      console.error("Error by un-assigning by API");
    }
    this.clearForm();
  }

  activeChanged() {
    this.form.active = !this.form.active;
    const message = this.form.active
      ? CONFIRM_DIALOG_FORM_SET_ACTIVE
      : CONFIRM_DIALOG_FORM_SET_INACTIVE;
    const dialogData = new ConfirmDialogModel(
      CONFIRM_DIALOG_CONFIRM_ACTION,
      message,
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((dialogResult) => {
      const cmd: FormSetActiveCommandAPI = {
        formUuid: this.form.uuid,
        active: this.form.active,
      };
      if (dialogResult) {
        this.formsAPIService.formUpdateActive(cmd).subscribe({
          next: (response) => {
            this.httpResponseReaction(response);
          },
          error: () => {
            this.snackBar.open(SNACKBAR_FORM_ASSIGN_ERROR, "", {
              duration: 2000,
              panelClass: "app-snackbar-alert",
            });
            console.error("Error by un-assigning by API");
          },
        });
      } else {
        this.form.active = !this.form.active;
      }
    });
  }

  allObjectsChanged() {
    this.form.allObjects = !this.form.allObjects;
    const message = this.form.allObjects
      ? CONFIRM_DIALOG_FORM_SET_ALL_OBJECT
      : CONFIRM_DIALOG_FORM_SET_ALL_SELECTED_OBJ;
    const dialogData = new ConfirmDialogModel(
      CONFIRM_DIALOG_CONFIRM_ACTION,
      message,
    );
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((dialogResult) => {
      const cmd: ChangeFormPermissionsCommand = {
        templateUuid: this.form.uuid,
        permissions: null,
      };
      if (dialogResult) {
        this.formsAPIService.formUpdatePermissions(cmd).subscribe({
          next: (response) => {
            this.httpResponseReaction(response);
          },
          error: () => {
            this.snackBar.open(SNACKBAR_FORM_ASSIGN_ERROR, "", {
              duration: 2000,
              panelClass: "app-snackbar-alert",
            });
            console.error("Error by un-assigning by API");
          },
        });
      } else {
        this.form.allObjects = !this.form.allObjects;
      }
    });
  }

  savePdfTemplate(uuid: string) {
    if (this.base64textString === "") {
      return;
    }
    const cmd: UpdateFormFileCommand = {
      formUuid: uuid,
      templateBase64: this.base64textString,
    };
    this.formsAPIService
      .formUpdatePdfTemplate(cmd)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.httpResponseReaction(response);
        },
        error: () => {
          this.snackBar.open(SNACKBAR_FORM_ASSIGN_ERROR, "", {
            duration: 2000,
            panelClass: "app-snackbar-alert",
          });
          console.error("Error by un-assigning by API");
        },
      });
  }

  clearAllSelections() {
    this.currentObjectList = [];
  }

  changeExcluded() {
    this.isExcludedMode = !this.isExcludedMode;
  }

  onSelectedAction(actionType: ActionType) {
    this.selectedAction = ActionType[actionType];
    if (this.selectedAction === ActionType.EDIT) {
      if (this.firstSelect) {
        this.selectedPermissionObjectExcluded = cloneDeep(
          this.editExcludedMemory,
        );
        this.selectedPermissionObjectIncluded = cloneDeep(
          this.editIncludedMemory,
        );
        this.firstSelect = false;
      } else {
        this.saveSavePermissionsToMemory();
      }
    } else if (this.selectedAction === ActionType.SAVE) {
      if (this.firstSelect) {
        this.selectedPermissionObjectExcluded = cloneDeep(
          this.saveExcludedMemory,
        );
        this.selectedPermissionObjectIncluded = cloneDeep(
          this.saveIncludedMemory,
        );
        this.firstSelect = false;
      } else {
        this.saveEditPermissionsToMemory();
      }
      this.permissionType = PermissionType.STATUS;
      this.currentObjectList = this.statuses;
      this.selectionForm.controls.selectedPermissionType.setValue(
        PermissionType.STATUS,
      );
    }

    // console.group("PERMISSIONS:");
    // console.log("EDIT-PERM-EX", this.editExcludedMemory);
    // console.log("EDIT-PERM-IN", this.editIncludedMemory);
    // console.log("SAVE-PERM-EX", this.saveExcludedMemory);
    // console.log("SAVE-PERM-IN", this.saveIncludedMemory);
    // console.groupEnd;
  }

  private saveEditPermissionsToMemory() {
    this.editExcludedMemory = cloneDeep(this.selectedPermissionObjectExcluded);
    this.editIncludedMemory = cloneDeep(this.selectedPermissionObjectIncluded);
    this.clearSelection();
    this.selectedPermissionObjectExcluded = cloneDeep(this.saveExcludedMemory);
    this.selectedPermissionObjectIncluded = cloneDeep(this.saveIncludedMemory);
  }

  private saveSavePermissionsToMemory() {
    this.saveExcludedMemory = cloneDeep(this.selectedPermissionObjectExcluded);
    this.saveIncludedMemory = cloneDeep(this.selectedPermissionObjectIncluded);
    this.clearSelection();
    this.selectedPermissionObjectExcluded = cloneDeep(this.editExcludedMemory);
    this.selectedPermissionObjectIncluded = cloneDeep(this.editIncludedMemory);
  }

  onSelectedPermissionType(permissionType: PermissionType) {
    this.permissionType = permissionType;

    switch (permissionType) {
      case PermissionType.CUSTOMER:
        this.currentObjectList = sortByStringWithEmptyAtEnd(
          this.customers,
          (item) => item.name,
        );
        break;
      case PermissionType.BRANCH:
        this.currentObjectList = sortByStringWithEmptyAtEnd(
          this.branches,
          (item) => item.name,
        );
        break;
      case PermissionType.DEPARTMENT:
        this.currentObjectList = sortByStringWithEmptyAtEnd(
          this.departments,
          (item) => item.name,
        );
        break;
      case PermissionType.STATUS:
        this.currentObjectList = [...this.statuses].sort(
          (a, b) => (a.metaId ?? 0) - (b.metaId ?? 0),
        );
        break;
      case PermissionType.PRODUCT:
        this.currentObjectList = sortByStringWithEmptyAtEnd(
          // this.products.filter((c) => c.model != null),
          this.products,
          (item) => item.model,
        );
        break;
      case PermissionType.PRODUCT_CATEGORY:
        this.currentObjectList = sortByStringWithEmptyAtEnd(
          this.prodCategories,
          (item) => item.name,
        );
        break;
      case PermissionType.ORDER_TYPE:
        this.currentObjectList = sortByStringWithEmptyAtEnd(
          this.orderTypes,
          (item) => item.name,
        );
        break;
      default:
        this.currentObjectList = [];
    }
    // this.clearAllSelections();
  }

  onCustomerSelected(customer: Customer) {
    if (customer == null) {
      return;
    }
    if (!this.isExcludedMode) {
      if (
        this.selectedPermissionObjectIncluded.findIndex(
          (p) => p.objUuid === customer.uuid,
        ) === -1
      ) {
        this.selectedPermissionObjectIncluded.push(
          new PermissionObject(
            this.permissionType,
            customer.name,
            customer.uuid,
          ),
        );
        this.selectedPermissionObjectIncluded =
          this.selectedPermissionObjectIncluded.sort((a, b) =>
            a.permissionType.localeCompare(b.permissionType),
          );
      }
    } else {
      if (
        this.selectedPermissionObjectExcluded.findIndex(
          (p) => p.objUuid === customer.uuid,
        ) === -1
      ) {
        this.selectedPermissionObjectExcluded.push(
          new PermissionObject(
            this.permissionType,
            customer.name,
            customer.uuid,
          ),
        );
        this.selectedPermissionObjectExcluded =
          this.selectedPermissionObjectExcluded.sort((a, b) =>
            a.permissionType.localeCompare(b.permissionType),
          );
      }
    }
  }

  onPermissionObjectSelection(
    $event: MatSelectionListChange,
    selectedOptions: SelectionModel<MatListOption>,
  ) {
    if (!this.isExcludedMode) {
      this.selectedPermissionObjectIncluded = this.handleSelectionEvent(
        $event,
        selectedOptions,
        this.selectedPermissionObjectIncluded,
      );
    } else {
      this.selectedPermissionObjectExcluded = this.handleSelectionEvent(
        $event,
        selectedOptions,
        this.selectedPermissionObjectExcluded,
      );
    }
  }

  handleSelectionEvent(
    $event: MatSelectionListChange,
    selectedOptions: SelectionModel<MatListOption>,
    selectedPermissionObject: PermissionObject[],
  ) {
    if (selectedOptions.isEmpty()) {
      selectedPermissionObject = selectedPermissionObject.filter(
        (sp) => sp.permissionType !== this.permissionType,
      );
      return selectedPermissionObject;
    }
    /// remove previous selection for this permission type
    selectedPermissionObject = selectedPermissionObject.filter(
      (sp) => sp.permissionType !== this.permissionType,
    );
    selectedOptions.selected.map((s) => {
      const uuid = s.value.uuid;
      let name: string;
      if (this.permissionType === PermissionType.PRODUCT) {
        name = s.value.model;
      } else {
        name = s.value.name;
      }
      selectedPermissionObject.push(
        new PermissionObject(this.permissionType, name, uuid),
      );
      selectedPermissionObject = selectedPermissionObject.sort((a, b) =>
        a.permissionType.localeCompare(b.permissionType),
      );
    });
    return selectedPermissionObject;
  }

  removePermissionObjectFromList(i: number) {
    if (!this.isExcludedMode) {
      this.selectedPermissionObjectIncluded.splice(i, 1);
    } else {
      this.selectedPermissionObjectExcluded.splice(i, 1);
    }
  }

  getLogicalOperator(i: number) {
    if (this.isExcludedMode) {
      return "or";
    } else if (
      this.selectedPermissionObjectIncluded.length > 1 &&
      i < this.selectedPermissionObjectIncluded.length - 1
    ) {
      if (
        this.selectedPermissionObjectIncluded[i].permissionType ===
        this.selectedPermissionObjectIncluded[i + 1].permissionType
      ) {
        return "or";
      } else {
        return "and";
      }
    }
  }

  checkIfOptionSelected(uuid: string) {
    if (this.selectedPermissionObjectIncluded.length > 0) {
      if (!this.isExcludedMode) {
        return (
          this.selectedPermissionObjectIncluded.findIndex(
            (sp) => sp.objUuid === uuid,
          ) !== -1
        );
      } else {
        return (
          this.selectedPermissionObjectExcluded.findIndex(
            (sp) => sp.objUuid === uuid,
          ) !== -1
        );
      }
    }
  }

  saveFormAssignment() {
    if (this.selectedAction === ActionType.SAVE) {
      this.saveExcludedMemory = cloneDeep(
        this.selectedPermissionObjectExcluded,
      );
      this.saveIncludedMemory = cloneDeep(
        this.selectedPermissionObjectIncluded,
      );
    }
    if (this.selectedAction === ActionType.EDIT) {
      this.editExcludedMemory = cloneDeep(
        this.selectedPermissionObjectExcluded,
      );
      this.editIncludedMemory = cloneDeep(
        this.selectedPermissionObjectIncluded,
      );
    }
    const permissions: Map<ActionType, PermissionStringMap> = new Map<
      ActionType,
      PermissionStringMap
    >();

    this.savePermissionMap.excluded = [];
    this.editPermissionMap.excluded = [];
    this.saveExcludedMemory.forEach((value) => {
      this.savePermissionMap.excluded.push(
        value.permissionType.valueOf() + "$" + value.objUuid,
      );
    });

    this.editExcludedMemory.forEach((value) => {
      this.editPermissionMap.excluded.push(
        value.permissionType.valueOf() + "$" + value.objUuid,
      );
    });

    this.editPermissionMap.included = [];
    this.savePermissionMap.included = [];
    let temp: string[];
    const keys = Object.keys(PermissionType).filter((v) => isNaN(Number(v)));
    keys.forEach((key, index) => {
      temp = [];
      this.saveIncludedMemory.forEach((value) => {
        // console.log(key + ' ' + index);
        if (value.permissionType === PermissionType[key]) {
          console.log(value.permissionType.valueOf() + "$" + value.objUuid);
          temp.push(value.permissionType.valueOf() + "$" + value.objUuid);
        }
      });
      if (temp.length > 0) {
        this.savePermissionMap.included.push(cloneDeep(temp));
      }
      temp = [];
      this.editIncludedMemory.forEach((value) => {
        if (value.permissionType === PermissionType[key]) {
          temp.push(value.permissionType.valueOf() + "$" + value.objUuid);
        }
      });
      if (temp.length > 0) {
        this.editPermissionMap.included.push(cloneDeep(temp));
      }
    });
    permissions[ActionType.EDIT] = this.editPermissionMap;
    permissions[ActionType.SAVE] = this.savePermissionMap;
    const formPermissionsCommand: ChangeFormPermissionsCommand = {
      permissions,
      templateUuid: this.form.uuid,
    };

    this.formsAPIService
      .formUpdatePermissions(formPermissionsCommand)
      .subscribe({
        next: (response) => {
          console.log("POST next");

          this.httpResponseReaction(response);

          this.onGoToPrevious();
        },
        error: (err) => {
          console.error("POST error:", err);
        },
        complete: () => {
          console.log("Subscriber COMPLETE");
        },
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(1);
    this.destroyed$.complete();
  }

  isClearPermissionsButtonDisabled() {
    if (!this.isExcludedMode) {
      return this.selectedPermissionObjectIncluded.length > 0;
    } else {
      return this.selectedPermissionObjectExcluded.length > 0;
    }
  }

  clearSelection() {
    if (!this.isExcludedMode) {
      this.selectedPermissionObjectIncluded.splice(0);
    } else {
      this.selectedPermissionObjectExcluded.splice(0);
    }
  }

  isSubmitButtonDisabled() {
    if (this.selectedAction === ActionType.EDIT) {
      return (
        this.selectedPermissionObjectExcluded.length > 0 ||
        this.selectedPermissionObjectIncluded.length > 0 ||
        this.saveExcludedMemory.length > 0 ||
        this.saveIncludedMemory.length > 0
      );
    } else {
      return (
        this.selectedPermissionObjectExcluded.length > 0 ||
        this.selectedPermissionObjectIncluded.length > 0 ||
        this.editExcludedMemory.length > 0 ||
        this.editIncludedMemory.length > 0
      );
    }
  }

  loadPermission() {
    this.syncCurrentSelectionToMemory();
    const dialogRef = this.dialog.open(FormAssignPermDialogComponent, {
      minWidth: "25vw",
      data: {
        form: this.form,
        nameResolver: this.getObjectName.bind(this),
      },
    });
    dialogRef.afterClosed().subscribe((data) => {
      if (!data) {
        return;
      }
      this.assignPermission(data);
    });
  }

  private syncCurrentSelectionToMemory() {
    if (this.selectedAction === ActionType.EDIT) {
      this.editExcludedMemory = cloneDeep(
        this.selectedPermissionObjectExcluded,
      );
      this.editIncludedMemory = cloneDeep(
        this.selectedPermissionObjectIncluded,
      );
    } else if (this.selectedAction === ActionType.SAVE) {
      this.saveExcludedMemory = cloneDeep(
        this.selectedPermissionObjectExcluded,
      );
      this.saveIncludedMemory = cloneDeep(
        this.selectedPermissionObjectIncluded,
      );
    }
  }

  private assignPermission(newPermissions: any) {
    this.parsePermissionsToMemory(newPermissions);
    this.refreshViewSelection();
  }

  private parsePermissionsToMemory(newPermissions: any) {
    if (newPermissions?.edit?.included) {
      newPermissions.edit.included.forEach((arr: string[]) =>
        arr.forEach((item) => this.addIfUnique(this.editIncludedMemory, item)),
      );
    }
    if (newPermissions?.edit?.excluded) {
      newPermissions.edit.excluded.forEach((item: string) =>
        this.addIfUnique(this.editExcludedMemory, item),
      );
    }
    if (newPermissions?.save?.included) {
      newPermissions.save.included.forEach((arr: string[]) =>
        arr.forEach((item) => this.addIfUnique(this.saveIncludedMemory, item)),
      );
    }
    if (newPermissions?.save?.excluded) {
      newPermissions.save.excluded.forEach((item: string) =>
        this.addIfUnique(this.saveExcludedMemory, item),
      );
    }
  }

  private addIfUnique(memoryArray: PermissionObject[], itemStr: string) {
    const [typeStr, uuidStr] = itemStr.split("$");
    const exists = memoryArray.some(
      (p) => p.permissionType === typeStr && p.objUuid === uuidStr,
    );

    if (!exists) {
      const name = this.getObjectName(typeStr, uuidStr);
      memoryArray.push(
        new PermissionObject(typeStr as PermissionType, name, uuidStr),
      );
    }
  }

  private refreshViewSelection() {
    if (this.selectedAction === ActionType.EDIT) {
      this.selectedPermissionObjectIncluded = cloneDeep(
        this.editIncludedMemory,
      );
      this.selectedPermissionObjectExcluded = cloneDeep(
        this.editExcludedMemory,
      );
    } else if (this.selectedAction === ActionType.SAVE) {
      this.selectedPermissionObjectIncluded = cloneDeep(
        this.saveIncludedMemory,
      );
      this.selectedPermissionObjectExcluded = cloneDeep(
        this.saveExcludedMemory,
      );
    }
  }

  protected getObjectName(type: string, uuid: string): string {
    let obj: any;
    switch (type) {
      case PermissionType.CUSTOMER:
        obj = this.customers?.find((c) => c.uuid === uuid);
        break;
      case PermissionType.BRANCH:
        obj = this.branches?.find((c) => c.uuid === uuid);
        break;
      case PermissionType.DEPARTMENT:
        obj = this.departments?.find((c) => c.uuid === uuid);
        break;
      case PermissionType.STATUS:
        obj = this.statuses?.find((c) => c.uuid === uuid);
        break;
      case PermissionType.PRODUCT:
        obj = this.products?.find((c) => c.uuid === uuid);
        return obj ? obj.model : "---";
      case PermissionType.PRODUCT_CATEGORY:
        obj = this.prodCategories?.find((c) => c.uuid === uuid);
        break;
      case PermissionType.ORDER_TYPE:
        obj = this.orderTypes?.find((c) => c.uuid === uuid);
        break;
    }

    return obj && obj.name ? obj.name : "---";
  }
}

export interface PermissionList {
  included: string[];
  excluded: string[];
}

export interface PermissionsState {
  [action: string]: PermissionList;
}
